import _get from 'lodash/get';
import _orderBy from 'lodash/orderBy';
import { useEffect, useRef, useState } from 'react';
import { NavigateFunction } from 'react-router';
import { BrandClub } from '../../types/Campaign';
import { useDelayedRedirect } from './useDelayedRedirect';

export type JoinBrandClubState = 'not_join' | 'joining' | 'joined';

export const useJoinBrandClub = (
  brand: Partial<BrandClub> | undefined,
  handleJoinClub: ((brandclubId: string, campaignId?: string) => Promise<'success' | 'failed' | undefined>) | undefined,
) => {
  const [joinedState, setJoinedState] = useState<JoinBrandClubState>(brand?.isMember ? 'joined' : 'not_join');

  const campaignRewards = _orderBy(_get(brand, ['campaignRewards']), ['rewardAmount'], ['desc']);

  const handleJoinError = (errorMessage: string) => {
    console.error('join club error: ', errorMessage);
    setJoinedState('not_join');
  };

  const handleJoin = async (e?: React.UIEvent) => {
    if (!brand?.brandclubId) {
      handleJoinError('brandclubId is not defined');
      return;
    }
    e?.stopPropagation?.();
    e?.preventDefault?.();
    try {
      setJoinedState('joining');
      const campaignId = _get(campaignRewards, ['0', 'campaignId'], undefined);
      const res = await handleJoinClub?.(brand.brandclubId, campaignId);
      if (res === 'success') {
        setJoinedState('joined');
      } else {
        handleJoinError('Unknown error');
      }
    } catch (e) {
      if (e instanceof Error) {
        handleJoinError(e.message);
      } else {
        handleJoinError('Unknown error');
      }
    }
  };

  return {
    joinedState,
    handleJoin,
    campaignRewards,
  };
};

interface JoinBrandClubWithRedirectOptions {
  handleJoinClub: ((brandclubId: string, campaignId?: string) => Promise<'success' | 'failed' | undefined>) | undefined;
  brand: Partial<BrandClub> | undefined;
  delay?: number;
  link?: string;
  navigate: NavigateFunction;
}
export const useJoinBrandClubWithRedirect = ({
  brand,
  handleJoinClub,
  link,
  delay = 500,
  navigate,
}: JoinBrandClubWithRedirectOptions) => {
  const { joinedState, handleJoin, ...rest } = useJoinBrandClub(brand, handleJoinClub);
  const prevJoinedStateRef = useRef(joinedState);
  const delayedRedirect = useDelayedRedirect(navigate, delay);
  const brandId = brand?.brandId;

  useEffect(() => {
    // only redirect if we went from joining to joined
    if (brandId && prevJoinedStateRef.current === 'joining' && joinedState === 'joined') {
      delayedRedirect(link || `/joinclubsuccess/${brandId}`);
    }
    prevJoinedStateRef.current = joinedState;
  }, [joinedState, delayedRedirect, link, brandId]);

  return {
    joinedState,
    handleJoin,
    ...rest,
  };
};
